import { CopyOutlined, DownloadOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Spin, Tooltip, message } from "antd";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";

function escapeHtml(value) {
    if (value === null || value === undefined) return "";
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

// Build a styled, self-contained HTML report from the structured description.
// Used as a client-side fallback when the backend hasn't supplied reportHtml,
// so the rendered report, Copy and Download all behave like the other reports.
function buildHtmlFromSummary(summary) {
    if (!summary || !summary.description) return null;
    const d = summary.description || {};
    const dq = d.dataQualityAssessment || {};
    const coverage = summary.coverage || {};
    const redacted = Array.isArray(summary.redactedPiiFields) ? summary.redactedPiiFields : [];

    const has = (v) => typeof v === "string" && v.trim().length > 0;
    const textBlock = (value) =>
        has(value)
            ? escapeHtml(value)
                  .split(/\n{2,}/)
                  .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
                  .join("")
            : '<p class="muted">Not provided.</p>';
    const listBlock = (items, empty) => {
        const arr = Array.isArray(items) ? items.filter((i) => has(String(i))) : [];
        return arr.length
            ? `<ul>${arr.map((i) => `<li>${escapeHtml(String(i))}</li>`).join("")}</ul>`
            : `<p class="muted">${escapeHtml(empty || "None provided.")}</p>`;
    };
    const vizBlock = (items) => {
        const arr = Array.isArray(items) ? items : [];
        return arr.length
            ? `<ul>${arr
                  .map((v) => {
                      const chart = v && has(v.chart) ? `<span class="badge badge-info">${escapeHtml(v.chart)}</span> ` : "";
                      const fields = v && Array.isArray(v.fields) && v.fields.length ? `<code>${escapeHtml(v.fields.join(", "))}</code>` : "";
                      const rationale = v && has(v.rationale) ? ` — ${escapeHtml(v.rationale)}` : "";
                      return `<li>${chart}${fields}${rationale}</li>`;
                  })
                  .join("")}</ul>`
            : '<p class="muted">None provided.</p>';
    };

    const generatedAt = summary.generatedAt ? new Date(summary.generatedAt).toLocaleString() : new Date().toLocaleString();
    const warningBanner = has(summary.warningMessage) ? `<div class="banner banner-warning">${escapeHtml(summary.warningMessage)}</div>` : "";
    const piiBanner =
        redacted.length > 0
            ? `<div class="banner banner-info">PII protected: raw values for ${redacted.length} field(s) were withheld from the model (${escapeHtml(redacted.slice(0, 10).join(", "))}${redacted.length > 10 ? "…" : ""}).</div>`
            : "";
    const issuesBlock = Array.isArray(dq.issues) && dq.issues.length ? `<p class="muted">Issues</p>${listBlock(dq.issues, "")}` : "";

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Dataset AI Summary — ${escapeHtml(summary.dataSetName || "Dataset")}</title>
  <style>
    *{box-sizing:border-box}
    body{font-family:'Segoe UI',Arial,Helvetica,sans-serif;background:#f8fafc;margin:0 auto;padding:24px;color:#0f172a;max-width:960px}
    .card{background:#fff;border:1px solid #e2e8f0;border-radius:12px;padding:24px;margin-bottom:16px}
    .muted{color:#64748b;font-size:13px}
    .row{display:flex;gap:12px;flex-wrap:wrap;margin:12px 0}
    .kpi{flex:1;min-width:120px;border:1px solid #e2e8f0;border-radius:10px;padding:12px;text-align:center}
    .kpi .value{font-size:22px;font-weight:700;margin:4px 0;color:#4338ca}
    .banner{padding:12px 16px;border-radius:10px;margin:12px 0;font-size:13px}
    .banner-warning{background:#fffbeb;border:1px solid #fcd34d;color:#92400e}
    .banner-info{background:#eef2ff;border:1px solid #c7d2fe;color:#3730a3}
    h1{margin:0 0 4px 0;font-size:22px}
    h2{margin:20px 0 8px;font-size:15px;color:#334155;text-transform:uppercase;letter-spacing:.4px}
    p{font-size:14px;line-height:1.6;margin:0 0 10px}
    ul{margin:6px 0 12px 20px;padding:0} li{font-size:14px;line-height:1.6;margin:0 0 6px}
    code{background:#f1f5f9;padding:1px 6px;border-radius:4px;font-size:12px}
    .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600}
    .badge-info{background:#eef2ff;color:#3730a3}
    .dq dt{font-weight:600;font-size:13px;margin-top:6px} .dq dd{margin:0 0 6px;font-size:14px;color:#334155}
    .footer{margin-top:16px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:12px;color:#94a3b8;text-align:center}
  </style>
</head>
<body>
  <div class="card">
    <div class="muted">Pretectum CMDM — Dataset Intelligence</div>
    <h1>${escapeHtml(summary.dataSetName || "Dataset")} — AI Summary</h1>
    <div class="muted">Generated: ${escapeHtml(generatedAt)}${d.fallback ? " &nbsp;|&nbsp; Statistics-based (AI narrative unavailable)" : ""}</div>
    ${warningBanner}
    ${piiBanner}
    <div class="row">
      <div class="kpi"><div class="muted">Records</div><div class="value">${Number(summary.totalRecords || 0).toLocaleString()}</div></div>
      <div class="kpi"><div class="muted">Data Version</div><div class="value">v${escapeHtml(String(summary.recordSetVersion == null ? 0 : summary.recordSetVersion))}</div></div>
      <div class="kpi"><div class="muted">Profiled Fields</div><div class="value">${Number(coverage.profiledFieldsCount || 0)}</div></div>
      <div class="kpi"><div class="muted">Total Fields</div><div class="value">${Number(coverage.totalFields || 0)}</div></div>
    </div>
    <h2>Executive Summary</h2>
    ${textBlock(d.executiveSummary)}
    <h2>Detailed Description</h2>
    ${textBlock(d.detailedDescription)}
    <h2>Potential Use Cases</h2>
    ${listBlock(d.potentialUseCases, "No use cases suggested.")}
    <h2>Data Quality Assessment</h2>
    ${textBlock(dq.overall)}
    <dl class="dq">
      ${has(dq.completeness) ? `<dt>Completeness</dt><dd>${escapeHtml(dq.completeness)}</dd>` : ""}
      ${has(dq.uniqueness) ? `<dt>Uniqueness</dt><dd>${escapeHtml(dq.uniqueness)}</dd>` : ""}
      ${has(dq.validity) ? `<dt>Validity</dt><dd>${escapeHtml(dq.validity)}</dd>` : ""}
    </dl>
    ${issuesBlock}
    <h2>Visualization Suggestions</h2>
    ${vizBlock(d.visualizationSuggestions)}
    <h2>Recommendations</h2>
    ${listBlock(d.recommendations, "No recommendations provided.")}
    <div class="footer">Auto-generated by Pretectum CMDM AI from dataset profiling statistics. Validate before acting on recommendations.</div>
  </div>
</body>
</html>`;
}

const DatasetDescriptionModal = ({ open, onClose, summary, loading, error, errorMessage, onRegenerate }) => {
    const iframeRef = useRef(null);

    // Prefer the backend-rendered report; fall back to a client-built one.
    const effectiveHtml = summary && summary.reportHtml ? summary.reportHtml : buildHtmlFromSummary(summary);

    const extractTextFromHtml = (html) => {
        if (!html) return "";
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        return doc.body.textContent || "";
    };

    const handleCopyText = () => {
        if (!effectiveHtml) return;
        navigator.clipboard
            .writeText(extractTextFromHtml(effectiveHtml).replace(/\n{3,}/g, "\n\n").trim())
            .then(() => message.success("Dataset summary copied to clipboard"))
            .catch(() => message.error("Failed to copy to clipboard"));
    };

    const adjustIframeHeight = useCallback(() => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentDocument && iframe.contentDocument.body) {
            const height = iframe.contentDocument.body.scrollHeight;
            iframe.style.height = `${height + 20}px`;
        }
    }, []);

    useEffect(() => {
        if (effectiveHtml && iframeRef.current) {
            const iframe = iframeRef.current;
            const handleLoad = () => adjustIframeHeight();
            iframe.addEventListener("load", handleLoad);
            return () => iframe.removeEventListener("load", handleLoad);
        }
    }, [effectiveHtml, adjustIframeHeight]);

    const handleDownloadPdf = useCallback(() => {
        if (!effectiveHtml) return;
        const printWindow = window.open("", "_blank");
        if (!printWindow) return;
        printWindow.document.write(effectiveHtml);
        printWindow.document.close();
        printWindow.onload = () => {
            setTimeout(() => printWindow.print(), 300);
        };
    }, [effectiveHtml]);

    const sanitizedHtml = effectiveHtml
        ? effectiveHtml.replace(
              "</head>",
              "<style>html,body{overflow-x:hidden;max-width:100%;word-wrap:break-word}table{table-layout:fixed;width:100%;word-break:break-word}td,th{overflow-wrap:break-word}</style></head>",
          )
        : effectiveHtml;

    const showReportActions = !!effectiveHtml && !loading && !error;

    return (
        <Modal
            title={
                <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold">✨ Dataset AI Summary</span>
                </div>
            }
            open={open}
            onCancel={onClose}
            width={1000}
            centered
            footer={
                <div className="flex justify-between">
                    <Button type="default" icon={<ReloadOutlined />} onClick={onRegenerate} loading={loading} disabled={loading}>
                        Regenerate Summary
                    </Button>
                    <Space>
                        {showReportActions && (
                            <Tooltip title="Copy summary to clipboard">
                                <Button type="default" icon={<CopyOutlined />} onClick={handleCopyText}>
                                    Copy
                                </Button>
                            </Tooltip>
                        )}
                        {showReportActions && (
                            <Button type="default" icon={<DownloadOutlined />} onClick={handleDownloadPdf}>
                                Download PDF
                            </Button>
                        )}
                        <Button type="primary" onClick={onClose}>
                            Close
                        </Button>
                    </Space>
                </div>
            }
            styles={{
                body: {
                    maxHeight: "75vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                    padding: 0,
                },
            }}
        >
            {loading ? (
                <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                    <Spin size="large" />
                    <div className="text-center">
                        <p className="text-base font-medium text-gray-700">Generating dataset AI summary…</p>
                        <p className="mt-1 text-sm text-gray-500">This can take up to a minute for first-time generation.</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 p-6">
                    <div className="text-center">
                        <p className="text-base font-medium text-red-600">Could not generate the dataset summary.</p>
                        <p className="mt-1 text-sm text-gray-500">{errorMessage || "Please try again or contact support if the issue persists."}</p>
                    </div>
                    <Button type="primary" icon={<ReloadOutlined />} onClick={onRegenerate}>
                        Retry
                    </Button>
                </div>
            ) : effectiveHtml ? (
                <iframe
                    ref={iframeRef}
                    sandbox="allow-same-origin"
                    srcDoc={sanitizedHtml}
                    title="Dataset AI Summary"
                    style={{ width: "100%", border: "none", overflow: "hidden" }}
                />
            ) : (
                <div className="flex min-h-[200px] items-center justify-center p-6">
                    <p className="text-sm text-gray-500">No summary yet.</p>
                </div>
            )}
        </Modal>
    );
};

DatasetDescriptionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    summary: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    onRegenerate: PropTypes.func.isRequired,
};

export default React.memo(DatasetDescriptionModal);
